import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  MEASURE_UNITS,
  MEASURE_UNIT_LABELS,
  formatCents,
  formatQty,
  type Category,
  type Item,
  type MeasureUnit,
  type Paginated,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField, StatTiles } from '@/components/ContextPanel';
import { ActiveBadge, StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Drawer } from '@/components/Modal';
import { CollapsibleSection, Field, FormSection } from '@/components/FormSection';
import { QtyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';

/**
 * Itens e materiais da obra.
 *
 * O custo médio NÃO é editável: ele nasce das entradas (média ponderada). O que
 * o operador define aqui é o mínimo — o gatilho que faz o item aparecer na
 * faixa de atenção antes de faltar cimento no meio da concretagem.
 */
export function ItensPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [unit, setUnit] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [belowMinimum, setBelowMinimum] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Item | null>(null);
  const [removing, setRemoving] = useState(false);

  const categoriesQuery = useQuery({
    queryKey: ['categories', 'ITEM'],
    queryFn: () => api.get<Paginated<Category>>('/categories', { scope: 'ITEM', perPage: 100 }),
    staleTime: 5 * 60_000,
  });

  const listQuery = useQuery({
    queryKey: ['items', { search, unit, categoryId, belowMinimum, page }],
    queryFn: () =>
      api.get<Paginated<Item>>('/items', {
        search: search || undefined,
        unit: unit || undefined,
        categoryId: categoryId || undefined,
        belowMinimum: belowMinimum ? 'true' : undefined,
        page,
        perPage: 25,
      }),
  });

  const detailQuery = useQuery({
    queryKey: ['item', selectedId],
    queryFn: () => api.get<Item>(`/items/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['items'] });
    void queryClient.invalidateQueries({ queryKey: ['item'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-balances'] });
  };

  const removeMutation = useMutation({
    mutationFn: () => api.delete(`/items/${selectedId}`),
    onSuccess: () => {
      setRemoving(false);
      setSelectedId(null);
      refresh();
    },
  });

  const item = detailQuery.data;

  const columns: Array<Column<Item>> = [
    { key: 'sku', label: 'Código', mono: true, strong: true, render: (row) => row.sku },
    { key: 'name', label: 'Item', render: (row) => row.name },
    { key: 'categoryName', label: 'Categoria', render: (row) => blank(row.categoryName) },
    { key: 'unit', label: 'Un.', mono: true, render: (row) => row.unit },
    {
      key: 'totalQtyMilli',
      label: 'Saldo',
      right: true,
      mono: true,
      render: (row) => (
        <span style={row.belowMinimum ? { color: T.amber, fontWeight: 600 } : undefined}>
          {formatQty(row.totalQtyMilli)}
        </span>
      ),
    },
    {
      key: 'minQtyMilli',
      label: 'Mínimo',
      right: true,
      mono: true,
      render: (row) => (row.minQtyMilli > 0 ? formatQty(row.minQtyMilli) : '—'),
    },
    {
      key: 'avgCostCents',
      label: 'Custo médio',
      right: true,
      mono: true,
      render: (row) => formatCents(row.avgCostCents),
    },
    {
      key: 'status',
      label: 'Situação',
      render: (row) =>
        !row.isActive ? (
          <ActiveBadge active={false} />
        ) : row.belowMinimum ? (
          <StatusBadge kind="warn">Abaixo do mínimo</StatusBadge>
        ) : (
          <StatusBadge kind="ok">Normal</StatusBadge>
        ),
    },
  ];

  const hasFilters = Boolean(search || unit || categoryId || belowMinimum);

  return (
    <>
      <WorkspaceHeader
        title="Itens e materiais"
        actions={
          can('itens:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Novo item
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por código, nome, NCM ou código de barras…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'category',
              label: 'Categoria',
              value: categoryId,
              options: (categoriesQuery.data?.data ?? []).map((category) => ({
                value: category.id,
                label: category.name,
              })),
              onChange: (value) => {
                setCategoryId(value);
                setPage(1);
              },
            },
            {
              key: 'unit',
              label: 'Unidade',
              value: unit,
              options: MEASURE_UNITS.map((value) => ({
                value,
                label: `${value} · ${MEASURE_UNIT_LABELS[value]}`,
              })),
              onChange: (value) => {
                setUnit(value);
                setPage(1);
              },
            },
          ]}
        >
          <button
            type="button"
            className={`filterbar__chip${belowMinimum ? ' is-active' : ''}`}
            onClick={() => {
              setBelowMinimum((v) => !v);
              setPage(1);
            }}
          >
            <AlertTriangle size={12} aria-hidden /> Abaixo do mínimo
          </button>
        </FilterBar>

        {listQuery.isPending && <TableSkeleton columns={8} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Itens de estoque"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setUnit('');
                    setCategoryId('');
                    setBelowMinimum(false);
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhum item cadastrado"
                  description="Cadastre cimento, areia, ferro — o que o parque consome."
                  action={
                    can('itens:criar') ? (
                      <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                        Novo item
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={item ? `${item.sku} · ${item.name}` : 'Carregando…'}
        subtitle={item ? MEASURE_UNIT_LABELS[item.unit] : undefined}
        tabs={['Resumo', 'Ficha']}
        footer={
          item ? (
            <>
              {can('itens:editar') && (
                <Button icon={Pencil} small onClick={() => setEditing(item)}>
                  Editar
                </Button>
              )}
              {can('itens:excluir') && (
                <Button variant="danger" icon={Trash2} small onClick={() => setRemoving(true)}>
                  Excluir
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {(tab) => {
          if (detailQuery.isPending) return <p className="muted">Carregando…</p>;
          if (!item) return <p className="muted">Não foi possível carregar.</p>;

          if (tab === 0) {
            return (
              <>
                <StatTiles
                  items={[
                    { label: 'Saldo total', value: `${formatQty(item.totalQtyMilli)} ${item.unit}` },
                    { label: 'Custo médio', value: formatCents(item.avgCostCents) },
                  ]}
                />
                <ReadField label="Última compra" value={formatCents(item.lastCostCents)} mono />
                <ReadField
                  label="Valor em estoque"
                  value={formatCents(Math.round((item.totalQtyMilli * item.avgCostCents) / 1000))}
                  mono
                />
                <ReadField
                  label="Mínimo"
                  value={item.minQtyMilli > 0 ? `${formatQty(item.minQtyMilli)} ${item.unit}` : '—'}
                  mono
                />
                <ReadField
                  label="Máximo"
                  value={item.maxQtyMilli ? `${formatQty(item.maxQtyMilli)} ${item.unit}` : '—'}
                  mono
                />
                {item.belowMinimum && (
                  <div style={{ marginTop: 10 }}>
                    <StatusBadge kind="warn">Saldo abaixo do mínimo definido</StatusBadge>
                  </div>
                )}
              </>
            );
          }

          return (
            <>
              <ReadField label="Descrição" value={blank(item.description)} />
              <ReadField label="Categoria" value={blank(item.categoryName)} />
              <ReadField label="Unidade" value={`${item.unit} · ${MEASURE_UNIT_LABELS[item.unit]}`} />
              <ReadField label="NCM" value={blank(item.ncm)} mono />
              <ReadField label="Código de barras" value={blank(item.barcode)} mono />
              <ReadField label="Lote e validade" value={item.tracksBatch ? 'Controla' : 'Não controla'} />
              <ReadField label="Situação" value={<ActiveBadge active={item.isActive} />} />
            </>
          );
        }}
      </ContextPanel>

      <ItemForm
        open={creating || Boolean(editing)}
        item={editing}
        categories={categoriesQuery.data?.data ?? []}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          refresh();
        }}
      />

      <ConfirmDialog
        open={removing}
        destructive
        loading={removeMutation.isPending}
        title="Excluir este item?"
        confirmLabel="Excluir"
        message={
          <>
            Itens com saldo ou com movimentações não podem ser excluídos —
            desative-os. Nenhuma movimentação já registrada é apagada.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRemoving(false);
          removeMutation.reset();
        }}
        onConfirm={() => removeMutation.mutate()}
      />
    </>
  );
}

function ItemForm({
  open,
  item,
  categories,
  onClose,
  onSaved,
}: {
  open: boolean;
  item: Item | null;
  categories: Category[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [unit, setUnit] = useState<MeasureUnit>('UN');
  const [categoryId, setCategoryId] = useState('');
  const [minQtyMilli, setMinQtyMilli] = useState<number | null>(0);
  const [maxQtyMilli, setMaxQtyMilli] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [ncm, setNcm] = useState('');
  const [barcode, setBarcode] = useState('');
  const [tracksBatch, setTracksBatch] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? (item?.id ?? 'new') : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setSku(item?.sku ?? '');
    setName(item?.name ?? '');
    setUnit(item?.unit ?? 'UN');
    setCategoryId(item?.categoryId ?? '');
    setMinQtyMilli(item?.minQtyMilli ?? 0);
    setMaxQtyMilli(item?.maxQtyMilli ?? null);
    setDescription(item?.description ?? '');
    setNcm(item?.ncm ?? '');
    setBarcode(item?.barcode ?? '');
    setTracksBatch(item?.tracksBatch ?? false);
    setIsActive(item?.isActive ?? true);
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => (item ? api.patch(`/items/${item.id}`, body) : api.post('/items', body)),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={item ? 'Editar item' : 'Novo item'}
      description="Custo médio e saldo são calculados pelas movimentações"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={sku.trim() === '' || name.trim().length < 2}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                sku: sku.trim().toUpperCase(),
                name: name.trim(),
                unit,
                categoryId: categoryId || null,
                minQtyMilli: minQtyMilli ?? 0,
                maxQtyMilli: maxQtyMilli ?? null,
                description: description.trim() || null,
                ncm: ncm.trim() || null,
                barcode: barcode.trim() || null,
                tracksBatch,
                isActive,
              })
            }
          >
            {item ? 'Salvar' : 'Cadastrar'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <div className="grid-2">
          <Field label="Código (SKU)" htmlFor="item-sku" error={errors.sku}>
            <input
              id="item-sku"
              className="input input--mono"
              value={sku}
              onChange={(event) => setSku(event.target.value.toUpperCase())}
              placeholder="CIM-CP2-50"
            />
          </Field>
          <Field label="Unidade" htmlFor="item-unit">
            <select
              id="item-unit"
              className="input"
              value={unit}
              onChange={(event) => setUnit(event.target.value as MeasureUnit)}
            >
              {MEASURE_UNITS.map((value) => (
                <option key={value} value={value}>
                  {value} · {MEASURE_UNIT_LABELS[value]}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Nome" htmlFor="item-name" error={errors.name}>
          <input
            id="item-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Cimento CP-II 50kg"
          />
        </Field>

        <Field label="Categoria" htmlFor="item-category">
          <select
            id="item-category"
            className="input"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
          >
            <option value="">Sem categoria</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid-2">
          <Field
            label="Estoque mínimo"
            htmlFor="item-min"
            error={errors.minQtyMilli}
            hint="Abaixo disso, o item aparece na faixa de atenção"
          >
            <QtyInput id="item-min" value={minQtyMilli} onChange={setMinQtyMilli} unit={unit} />
          </Field>
          <Field label="Estoque máximo" htmlFor="item-max">
            <QtyInput id="item-max" value={maxQtyMilli} onChange={setMaxQtyMilli} unit={unit} />
          </Field>
        </div>
      </FormSection>

      <CollapsibleSection label="Mais informações (descrição, fiscal)">
        <Field label="Descrição" htmlFor="item-description">
          <textarea
            id="item-description"
            className="input"
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <div className="grid-2">
          <Field label="NCM" htmlFor="item-ncm">
            <input
              id="item-ncm"
              className="input input--mono"
              value={ncm}
              onChange={(event) => setNcm(event.target.value)}
            />
          </Field>
          <Field label="Código de barras" htmlFor="item-barcode">
            <input
              id="item-barcode"
              className="input input--mono"
              value={barcode}
              onChange={(event) => setBarcode(event.target.value)}
            />
          </Field>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={tracksBatch}
            onChange={(event) => setTracksBatch(event.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            Controla lote e validade
            <span className="muted" style={{ display: 'block', fontSize: 12 }}>
              Cloro, alimento, remédio. Toda entrada vai pedir o lote da embalagem, e a saída tira
              primeiro o que vence antes.
            </span>
          </span>
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
          Item ativo
        </label>
      </CollapsibleSection>
    </Drawer>
  );
}
